"use client";

import { createConfig, http } from "wagmi";
import { mainnet, bsc, base, polygon, optimism } from "wagmi/chains";
import { injected, walletConnect } from "wagmi/connectors";
import { Network } from "@aptos-labs/ts-sdk";

/**
 * Wallet configuration.
 *
 * No single library covers EVM + Solana + Aptos: Reown AppKit stops at EVM/Solana/Bitcoin,
 * and RainbowKit is EVM-only. So we run one adapter stack per ecosystem behind our own
 * modal, which is what PancakeSwap does for the same reason (and it suits the bespoke design
 * anyway). See BUILD_PLAN §2.3.
 *
 * Note: wagmi v3 is fine here precisely BECAUSE the modal is ours: the usual "pin v2" advice
 * exists to stay compatible with RainbowKit/AppKit, and we depend on neither.
 */

// WalletConnect is optional: without a project id we simply don't offer it, rather than
// booting a connector that fails at click time. Injected/EIP-6963 wallets (MetaMask, Trust,
// Rabby, …) keep working regardless, that's the majority of desktop users.
export const WC_PROJECT_ID = process.env.NEXT_PUBLIC_WC_PROJECT_ID ?? "";
export const hasWalletConnect = WC_PROJECT_ID.length > 0;

// The origin WalletConnect advertises to the relay/Verify. Must equal where the page is
// actually served, so it's read at runtime; the env value is only an SSR fallback.
const WC_APP_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : process.env.NEXT_PUBLIC_SITE_URL ?? "https://swap.dubupay.com";

// The chains we accept signatures from. Sign-in is chain-agnostic (a signature is a
// signature), but the connector needs a chain list, and these mirror dubu-api's
// SUPPORTED_CHAINS for deposits.
export const wagmiConfig = createConfig({
  chains: [mainnet, polygon, base, optimism, bsc],
  connectors: [
    // EIP-6963 discovery, surfaces every installed browser wallet without hardcoding them.
    injected({ shimDisconnect: true }),
    ...(hasWalletConnect
      ? [
          walletConnect({
            projectId: WC_PROJECT_ID,
            showQrModal: true,
            metadata: {
              name: "Dubu Swap",
              description: "Trade USD/NGN with your wallet",
              // MUST match the actual page origin. Reown's relay/Verify rejects a metadata.url
              // that differs from where the page is served, a hardcoded swap.dubupay.com on
              // localhost (or any preview URL) closes the relay handshake and no QR is ever
              // generated. Use the live origin at runtime; fall back to the configured site URL
              // only during SSR where window is absent.
              url: WC_APP_URL,
              icons: [`${WC_APP_URL}/assets/dubu-icon.png`],
            },
          }),
        ]
      : []),
  ],
  transports: {
    [mainnet.id]: http(),
    [polygon.id]: http(),
    [base.id]: http(),
    [optimism.id]: http(),
    [bsc.id]: http(),
  },
  // ssr:true is only correct when paired with cookieStorage + cookieToInitialState() feeding
  // <WagmiProvider initialState>. Without that wiring it reconnects from persisted client
  // state on mount, which is a known source of hydration/reconnect crashes. This app renders
  // wallet state entirely client-side, so ssr:false is both correct and simpler.
  ssr: false,
});

export const SOLANA_RPC =
  process.env.NEXT_PUBLIC_SOLANA_RPC_URL ?? "https://api.mainnet-beta.solana.com";

// The adapter wants the SDK's Network enum, not a bare string, map explicitly rather than
// casting, so an unknown env value falls back to mainnet instead of failing at runtime.
export const APTOS_NETWORK: Network =
  process.env.NEXT_PUBLIC_APTOS_NETWORK === "testnet" ? Network.TESTNET : Network.MAINNET;
